import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CrudPage } from "../components/CrudPage";
import { StatusBadge, fmt } from "../components/bits";

const studentLabel = (s) => `${s.first_name} ${s.last_name}`;
const moneyCol = (key, label) => ({ key, label, render: (r) => fmt(r[key]) });
const boolCol = (key, label) => ({ key, label, render: (r) => (r[key] ? "Sí" : "No") });

export function Guardians() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <CrudPage title="Padres / Tutores" subtitle="Tutores registrados y sus estudiantes — clic en la fila para ver la ficha" endpoint="guardians" testid="guardians"
      onRowClick={(g) => navigate(`/padres/${g.id}`)}
      canWrite={["superadmin", "admin", "secretaria"].includes(user?.role)} canDelete={["superadmin", "admin"].includes(user?.role)}
      columns={[
        { key: "name", label: "Nombre", render: (g) => <span className="font-medium text-slate-900">{g.name} {g.last_name}</span> },
        { key: "document", label: "Cédula", render: (g) => <span className="font-mono text-xs">{g.document}</span> },
        { key: "relationship", label: "Parentesco" },
        { key: "phone", label: "Teléfono" },
        { key: "email", label: "Correo" },
        { key: "student_ids", label: "Estudiantes", render: (g) => (g.student_ids || []).length },
        boolCol("is_authorized", "Autorizado"),
      ]}
      fields={[
        { key: "name", label: "Nombre", type: "text", required: true },
        { key: "last_name", label: "Apellido", type: "text" },
        { key: "document", label: "Cédula/Documento", type: "text" },
        { key: "phone", label: "Teléfono", type: "text" },
        { key: "whatsapp", label: "WhatsApp", type: "text" },
        { key: "email", label: "Correo", type: "email" },
        { key: "address", label: "Dirección", type: "textarea" },
        { key: "relationship", label: "Parentesco", type: "select", options: ["Madre", "Padre", "Tutor", "Abuelo/a", "Otro"].map((r) => ({ value: r, label: r })) },
        { key: "student_ids", label: "Estudiantes a su cargo", type: "multi-api", source: "students", optionLabel: studentLabel },
        { key: "is_authorized", label: "Contacto autorizado", type: "switch" },
      ]} />
  );
}

export function Teachers() {
  return (
    <CrudPage title="Maestros" subtitle="Maestros y sus materias asignadas" endpoint="teachers" base="/users" listQuery="?role=maestro" testid="teachers" canDelete={false}
      columns={[
        { key: "name", label: "Nombre", render: (t) => <span className="font-medium text-slate-900">{t.name}</span> },
        { key: "email", label: "Correo" },
        { key: "subject_ids", label: "Materias", render: (t) => (t.subject_ids || []).length },
        { key: "active", label: "Estado", render: (t) => <StatusBadge value={t.active ? "activo" : "retirado"} /> },
      ]}
      fields={[
        { key: "name", label: "Nombre", type: "text", required: true },
        { key: "email", label: "Correo", type: "email", required: true },
        { key: "password", label: "Contraseña (vacío = Demo123!)", type: "text" },
        { key: "subject_ids", label: "Materias asignadas", type: "multi-api", source: "r/subjects" },
      ]}
      transformPayload={(p) => ({ ...p, role: "maestro", ...(p.password ? {} : { password: undefined }) })} />
  );
}

export function Subjects() {
  const { user } = useAuth();
  return (
    <CrudPage title="Materias" subtitle="Asignaturas del colegio" endpoint="subjects" testid="subjects"
      canWrite={["superadmin", "admin", "secretaria"].includes(user?.role)} canDelete={["superadmin", "admin"].includes(user?.role)}
      columns={[
        { key: "name", label: "Materia", render: (s) => <span className="font-medium text-slate-900">{s.name}</span> },
        { key: "grade", label: "Nivel" },
        { key: "teacher_ids", label: "Maestros", render: (s) => (s.teacher_ids || []).length },
        { key: "active", label: "Estado", render: (s) => <StatusBadge value={s.active ? "activo" : "retirado"} /> },
      ]}
      fields={[
        { key: "name", label: "Nombre", type: "text", required: true },
        { key: "grade", label: "Nivel/Grado", type: "text" },
        { key: "teacher_ids", label: "Maestros asignados", type: "multi-api", source: "users?role=maestro" },
        { key: "active", label: "Activa", type: "switch" },
      ]} />
  );
}

export function Courses() {
  return (
    <CrudPage title="Cursos / Grados" subtitle="Niveles académicos y secciones" endpoint="courses" testid="courses" canDelete
      columns={[
        { key: "name", label: "Curso", render: (c) => <span className="font-medium text-slate-900">{c.name}</span> },
        { key: "level", label: "Nivel" },
        { key: "sections", label: "Secciones", render: (c) => (c.sections || []).join(", ") },
        { key: "active", label: "Estado", render: (c) => <StatusBadge value={c.active ? "activo" : "retirado"} /> },
      ]}
      fields={[
        { key: "name", label: "Nombre (ej: 3ro Primaria)", type: "text", required: true },
        { key: "level", label: "Nivel", type: "select", options: [{ value: "Primaria", label: "Primaria" }, { value: "Secundaria", label: "Secundaria" }, { value: "Inicial", label: "Inicial" }] },
        { key: "sections", label: "Secciones", type: "text" },
        { key: "active", label: "Activo", type: "switch" },
      ]}
      transformPayload={(p) => ({ ...p, sections: typeof p.sections === "string" ? p.sections.split(",").map((s) => s.trim()).filter(Boolean) : p.sections })} />
  );
}

export function Activities() {
  return (
    <CrudPage title="Actividades escolares" subtitle="Eventos, excursiones y actividades" endpoint="activities" testid="activities" canDelete
      columns={[
        { key: "name", label: "Actividad", render: (a) => <span className="font-medium text-slate-900">{a.name}</span> },
        { key: "date", label: "Fecha" },
        { key: "time", label: "Hora" },
        { key: "place", label: "Lugar" },
        { key: "category", label: "Categoría" },
        moneyCol("cost", "Costo"),
        { key: "status", label: "Estado", render: (a) => <StatusBadge value={(a.status || "programada").toLowerCase()} /> },
      ]}
      fields={[
        { key: "name", label: "Nombre", type: "text", required: true },
        { key: "description", label: "Descripción", type: "textarea" },
        { key: "date", label: "Fecha", type: "date" },
        { key: "time", label: "Hora", type: "text" },
        { key: "place", label: "Lugar", type: "text" },
        { key: "category", label: "Categoría", type: "select", options: ["Académica", "Deportiva", "Cultural", "Excursión", "Otra"].map((c) => ({ value: c, label: c })) },
        { key: "course", label: "Curso (vacío = todos)", type: "text" },
        { key: "cost", label: "Costo (RD$)", type: "number" },
        { key: "status", label: "Estado", type: "select", options: ["Programada", "Realizada", "Cancelada"].map((s) => ({ value: s, label: s })) },
      ]} />
  );
}

export function Concepts() {
  return (
    <CrudPage title="Conceptos de facturación" subtitle="Inscripción, mensualidad, transporte, etc." endpoint="concepts" testid="concepts" canDelete
      columns={[
        { key: "name", label: "Concepto", render: (c) => <span className="font-medium text-slate-900">{c.name}</span> },
        moneyCol("price", "Precio"),
        { key: "category", label: "Categoría" },
        { key: "active", label: "Estado", render: (c) => <StatusBadge value={c.active ? "activo" : "retirado"} /> },
      ]}
      fields={[
        { key: "name", label: "Nombre", type: "text", required: true },
        { key: "price", label: "Precio (RD$)", type: "number" },
        { key: "category", label: "Categoría", type: "select", options: ["Inscripción", "Mensualidad", "Actividades", "Servicios", "Uniformes", "Libros", "Combos", "Otros"].map((c) => ({ value: c, label: c })) },
        { key: "active", label: "Activo", type: "switch" },
      ]} />
  );
}

export function Discounts() {
  return (
    <CrudPage title="Descuentos" subtitle="Becas, hermanos, pronto pago y promociones" endpoint="discounts" testid="discounts" canDelete
      columns={[
        { key: "name", label: "Descuento", render: (d) => <span className="font-medium text-slate-900">{d.name}</span> },
        { key: "category", label: "Categoría" },
        { key: "percent", label: "Porcentaje", render: (d) => (d.percent ? `${d.percent}%` : "—") },
        { key: "fixed_amount", label: "Monto fijo", render: (d) => (d.fixed_amount ? fmt(d.fixed_amount) : "—") },
        { key: "end_date", label: "Vence" },
        { key: "active", label: "Estado", render: (d) => <StatusBadge value={d.active ? "activo" : "retirado"} /> },
      ]}
      fields={[
        { key: "name", label: "Nombre", type: "text", required: true },
        { key: "category", label: "Categoría", type: "select", options: ["Inscripción", "Hermanos", "Pronto pago", "Especial", "Beca", "Promocional", "Personalizado"].map((c) => ({ value: c, label: c })) },
        { key: "percent", label: "Porcentaje (%)", type: "number" },
        { key: "fixed_amount", label: "Monto fijo (RD$)", type: "number" },
        { key: "start_date", label: "Inicio", type: "date" },
        { key: "end_date", label: "Vencimiento", type: "date" },
        { key: "active", label: "Activo", type: "switch" },
      ]} />
  );
}

export function Combos() {
  return (
    <CrudPage title="Combos complementarios" subtitle="Paquetes de servicios para estudiantes" endpoint="combos" testid="combos" canDelete
      columns={[
        { key: "name", label: "Combo", render: (c) => <span className="font-medium text-slate-900">{c.name}</span> },
        { key: "items", label: "Incluye", render: (c) => <span className="text-xs text-slate-500">{(c.items || []).join(", ")}</span> },
        moneyCol("price", "Precio"),
        { key: "periodicity", label: "Periodicidad" },
        { key: "active", label: "Estado", render: (c) => <StatusBadge value={c.active ? "activo" : "retirado"} /> },
      ]}
      fields={[
        { key: "name", label: "Nombre", type: "text", required: true },
        { key: "description", label: "Descripción", type: "textarea" },
        { key: "items", label: "Elementos incluidos (separados por coma)", type: "text" },
        { key: "price", label: "Precio (RD$)", type: "number" },
        { key: "category", label: "Categoría", type: "select", options: ["Escolar", "Deportivo", "Académico", "Otro"].map((c) => ({ value: c, label: c })) },
        { key: "periodicity", label: "Periodicidad", type: "select", options: ["Único", "Mensual", "Trimestral", "Anual"].map((p) => ({ value: p, label: p })) },
        { key: "active", label: "Activo", type: "switch" },
      ]}
      transformPayload={(p) => ({ ...p, items: typeof p.items === "string" ? p.items.split(",").map((s) => s.trim()).filter(Boolean) : p.items })} />
  );
}

export function Expenses() {
  return (
    <CrudPage title="Gastos" subtitle="Gastos fijos, diarios, servicios y compras" endpoint="expenses" testid="expenses" canDelete
      columns={[
        { key: "concept", label: "Concepto", render: (e) => <span className="font-medium text-slate-900">{e.concept}</span> },
        { key: "category", label: "Categoría" },
        { key: "date", label: "Fecha" },
        { key: "provider", label: "Proveedor" },
        moneyCol("amount", "Monto"),
        { key: "method", label: "Método" },
      ]}
      fields={[
        { key: "concept", label: "Concepto", type: "text", required: true },
        { key: "category", label: "Categoría", type: "select", options: ["Gastos fijos", "Gastos diarios", "Servicios", "Nómina", "Compras", "Materiales", "Otros"].map((c) => ({ value: c, label: c })) },
        { key: "date", label: "Fecha", type: "date" },
        { key: "provider", label: "Proveedor", type: "text" },
        { key: "amount", label: "Monto (RD$)", type: "number" },
        { key: "method", label: "Método de pago", type: "select", options: ["Efectivo", "Transferencia", "Depósito", "Tarjeta", "Cheque"].map((m) => ({ value: m, label: m })) },
        { key: "description", label: "Descripción", type: "textarea" },
      ]} />
  );
}

export function Products() {
  return (
    <CrudPage title="Inventario" subtitle="Uniformes, libros y materiales" endpoint="products" testid="products" canDelete
      columns={[
        { key: "code", label: "Código", render: (p) => <span className="font-mono text-xs">{p.code}</span> },
        { key: "name", label: "Producto", render: (p) => <span className="font-medium text-slate-900">{p.name}</span> },
        { key: "category", label: "Categoría" },
        moneyCol("price", "Precio"),
        { key: "stock", label: "Existencia", render: (p) => <span className={`font-semibold ${p.stock < 10 ? "text-rose-600" : "text-slate-800"}`}>{p.stock}</span> },
      ]}
      fields={[
        { key: "code", label: "Código", type: "text" },
        { key: "name", label: "Nombre", type: "text", required: true },
        { key: "category", label: "Categoría", type: "select", options: ["Uniformes", "Libros", "Materiales", "Otros"].map((c) => ({ value: c, label: c })) },
        { key: "price", label: "Precio (RD$)", type: "number" },
        { key: "stock", label: "Existencia", type: "number" },
      ]} />
  );
}

export function BankAccounts() {
  return (
    <CrudPage title="Cuentas bancarias" subtitle="Cuentas visibles en el portal de padres" endpoint="bank-accounts" testid="banks" canDelete
      columns={[
        { key: "bank", label: "Banco", render: (b) => <span className="font-medium text-slate-900">{b.bank}</span> },
        { key: "name", label: "Nombre" },
        { key: "account_number", label: "Cuenta", render: (b) => <span className="font-mono text-xs">{b.account_number}</span> },
        { key: "account_type", label: "Tipo" },
        { key: "beneficiary", label: "Beneficiario" },
      ]}
      fields={[
        { key: "bank", label: "Banco", type: "text", required: true },
        { key: "name", label: "Nombre de la cuenta", type: "text" },
        { key: "account_number", label: "Número de cuenta", type: "text" },
        { key: "account_type", label: "Tipo", type: "select", options: ["Corriente", "Ahorros"].map((t) => ({ value: t, label: t })) },
        { key: "beneficiary", label: "Beneficiario", type: "text" },
        { key: "info", label: "Información adicional", type: "textarea" },
      ]} />
  );
}

export function Documents() {
  const [students, setStudents] = useState([]);
  useEffect(() => { api.get("/students").then((r) => setStudents(r.data)).catch(() => {}); }, []);
  const nameOf = (sid) => { const s = students.find((x) => x.id === sid); return s ? `${s.first_name} ${s.last_name}` : "—"; };
  return (
    <CrudPage title="Documentos y contratos" subtitle="Contratos de ingreso, autorizaciones y más" endpoint="documents" testid="documents" canDelete
      columns={[
        { key: "name", label: "Documento", render: (d) => <span className="font-medium text-slate-900">{d.name}</span> },
        { key: "doc_type", label: "Tipo" },
        { key: "student_id", label: "Estudiante", render: (d) => nameOf(d.student_id) },
        { key: "file_url", label: "Archivo", render: (d) => d.file_url ? <a href={d.file_url} target="_blank" rel="noreferrer" className="text-sky-600 underline">Ver</a> : "—" },
        boolCol("visible_to_parent", "Visible al padre"),
      ]}
      fields={[
        { key: "name", label: "Nombre", type: "text", required: true },
        { key: "doc_type", label: "Tipo", type: "select", options: ["Contrato de ingreso", "Documento administrativo", "Autorización", "Otro"].map((t) => ({ value: t, label: t })) },
        { key: "student_id", label: "Estudiante", type: "select-api", source: "students", optionLabel: studentLabel },
        { key: "file_url", label: "Archivo (PDF/imagen/Word)", type: "file", category: "documentos", accept: ".pdf,.docx,.txt,image/*" },
        { key: "visible_to_parent", label: "Visible en portal de padres", type: "switch", default: false },
      ]} />
  );
}
